import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOCPP } from '../contexts/OCPPContext';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';

const kw = (watts) => {
  if (watts == null || watts === '') return '—';
  const n = Number(watts);
  if (!Number.isFinite(n)) return '—';
  return `${(n / 1000).toFixed(1)} kW`;
};

const emptyForm = {
  id: 'sitterdorf',
  name: 'Sitterdorf',
  ems_site_id: 'sitterdorf',
  safety_margin_w: 40000,
  noise_margin_w: 5000,
  idle_floor_w: 7000,
  charger_max_w: 60000,
  pv_follow_min_w: 20000,
  control_mode: 'grid_assist',
  ems_token: '',
};

const Sites = () => {
  const { chargers } = useOCPP();
  const { addToast } = useToast();
  const api = apiClient;

  const [sites, setSites] = useState([]);
  const [selectedId, setSelectedId] = useState(undefined);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedChargers, setSelectedChargers] = useState([]);

  const chargerList = useMemo(() => Object.values(chargers || {}), [chargers]);

  const loadSites = useCallback(async () => {
    const resp = await api.get('/sites');
    setSites(resp.data.sites || []);
    return resp.data.sites || [];
  }, [api]);

  const loadStatus = useCallback(async (siteId) => {
    if (!siteId) {
      setStatus(null);
      return;
    }
    const resp = await api.get(`/sites/${encodeURIComponent(siteId)}/status`);
    setStatus(resp.data);
    const ids = resp.data.site?.charger_ids || [];
    setSelectedChargers(ids);
  }, [api]);

  useEffect(() => {
    loadSites().then((list) => {
      setSelectedId((current) => (current === undefined ? (list[0]?.id ?? null) : current));
    }).catch((err) => {
      addToast('Failed to load sites: ' + (err.response?.data?.detail || err.message), 'error');
    });
  }, [loadSites, addToast]);

  useEffect(() => {
    if (!selectedId) {
      setStatus(null);
      return undefined;
    }
    loadStatus(selectedId).catch(() => {});
    const timer = setInterval(() => {
      loadStatus(selectedId).catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
  }, [selectedId, loadStatus]);

  useEffect(() => {
    const site = sites.find((s) => s.id === selectedId);
    if (!site) return;
    setForm({
      id: site.id,
      name: site.name,
      ems_site_id: site.ems_site_id,
      safety_margin_w: site.safety_margin_w,
      noise_margin_w: site.noise_margin_w,
      idle_floor_w: site.idle_floor_w,
      charger_max_w: site.charger_max_w,
      pv_follow_min_w: site.pv_follow_min_w ?? 20000,
      control_mode: site.control_mode || 'grid_assist',
      ems_token: '',
    });
  }, [sites, selectedId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, auto_control: false };
      if (!payload.ems_token) delete payload.ems_token;
      await api.post('/sites', payload);
      addToast('Site created', 'success');
      const list = await loadSites();
      setSelectedId(form.id);
      if (!list.find((s) => s.id === form.id)) setSites((prev) => prev);
    } catch (err) {
      addToast('Failed to create site: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const patch = {
        name: form.name,
        ems_site_id: form.ems_site_id,
        safety_margin_w: parseInt(form.safety_margin_w, 10),
        noise_margin_w: parseInt(form.noise_margin_w, 10),
        idle_floor_w: parseInt(form.idle_floor_w, 10),
        charger_max_w: parseInt(form.charger_max_w, 10),
        pv_follow_min_w: parseInt(form.pv_follow_min_w, 10),
        control_mode: form.control_mode || 'grid_assist',
      };
      if ((form.ems_token || '').trim()) {
        patch.ems_token = form.ems_token.trim();
      }
      await api.patch(`/sites/${encodeURIComponent(selectedId)}`, patch);
      await api.put(`/sites/${encodeURIComponent(selectedId)}/chargers`, {
        charger_ids: selectedChargers,
      });
      addToast('Site saved', 'success');
      await loadSites();
      await loadStatus(selectedId);
    } catch (err) {
      addToast('Failed to save site: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAuto = async (enabled) => {
    if (!selectedId) return;
    try {
      await api.patch(`/sites/${encodeURIComponent(selectedId)}`, { auto_control: enabled });
      addToast(enabled ? 'Automatic PV control enabled' : 'Automatic PV control disabled', 'success');
      await loadSites();
    } catch (err) {
      addToast('Failed to change auto control: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm('Delete this site? Chargers are unassigned, not deleted.')) return;
    try {
      await api.delete(`/sites/${encodeURIComponent(selectedId)}`);
      addToast('Site deleted', 'success');
      const list = await loadSites();
      setSelectedId(list[0]?.id || null);
      setStatus(null);
    } catch (err) {
      addToast('Failed to delete site: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const toggleCharger = (id) => {
    setSelectedChargers((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const control = status?.control;
  const live = control?.live;
  const selectedSite = sites.find((s) => s.id === selectedId);

  return (
    <div>
      <h1>Sites</h1>
      <p style={{ color: '#555', marginTop: 0 }}>
        Group chargers by location and optionally drive TxDefault limits from EMS live PV/grid data.
        Allocation never exceeds the computed budget. Safety and noise margins are subtracted first.
      </p>

      {selectedId && selectedSite && !selectedSite.ems_token_configured && (
        <div className="card" style={{ background: '#fff8e1', border: '1px solid #ffe082', marginBottom: '1rem' }}>
          No EMS token on this site. Paste the EMS API token below and save to enable live PV data and auto control.
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <h3>Sites</h3>
          {sites.length === 0 && <p style={{ color: '#666' }}>No sites yet.</p>}
          {sites.map((site) => (
            <button
              key={site.id}
              type="button"
              onClick={() => setSelectedId(site.id)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                marginBottom: '0.5rem',
                padding: '0.75rem',
                borderRadius: 6,
                border: site.id === selectedId ? '2px solid #1976d2' : '1px solid #ddd',
                background: site.id === selectedId ? '#f0f7ff' : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600 }}>{site.name}</div>
              <div style={{ fontSize: '0.85rem', color: '#555' }}>
                {site.id} · {site.charger_ids?.length || 0} chargers · {site.control_mode === 'pv_follow' ? 'follow PV' : 'grid assist'} · {site.auto_control ? 'auto on' : 'auto off'}
                {site.ems_token_configured ? ' · token set' : ''}
              </div>
            </button>
          ))}

          <h3 style={{ marginTop: '1.5rem' }}>{selectedId ? 'Edit site' : 'New site'}</h3>
          <form onSubmit={(e) => { if (selectedId) { e.preventDefault(); handleSave(); } else { handleCreate(e); } }}>
            <label style={labelStyle}>Site id (EMS)</label>
            <input
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value, ems_site_id: e.target.value })}
              disabled={Boolean(selectedId)}
              style={inputStyle}
            />
            <label style={labelStyle}>Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
            <label style={labelStyle}>Control mode</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {[
                { id: 'grid_assist', title: 'Grid assist', desc: 'Base = grid limit − safety. Budget = base + PV. Ignores house/EV meters.' },
                { id: 'pv_follow', title: 'Follow PV', desc: 'Budget = max(min, PV − house excl. EV − noise). Min keeps charging from stopping (default 20 kW).' },
              ].map((mode) => {
                const selected = (form.control_mode || 'grid_assist') === mode.id;
                return (
                  <label
                    key={mode.id}
                    style={{
                      display: 'block',
                      padding: '0.75rem',
                      borderRadius: 6,
                      border: selected ? '2px solid #1976d2' : '1px solid #ddd',
                      background: selected ? '#f0f7ff' : '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="control-mode"
                      checked={selected}
                      onChange={() => setForm({ ...form, control_mode: mode.id })}
                      style={{ marginRight: '0.4rem' }}
                    />
                    <strong>{mode.title}</strong>
                    <div style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.35rem' }}>{mode.desc}</div>
                  </label>
                );
              })}
            </div>
            <label style={labelStyle}>EMS API token</label>
            <input
              type="password"
              autoComplete="off"
              value={form.ems_token}
              onChange={(e) => setForm({ ...form, ems_token: e.target.value })}
              placeholder={selectedSite?.ems_token_configured ? 'Stored — paste a new token to replace' : 'ems_tok_…'}
              style={inputStyle}
            />
            <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
              {selectedSite?.ems_token_configured
                ? `Token is stored on this site${selectedSite.ems_token_source === 'env' ? ' (env fallback)' : ''}. It is never shown again.`
                : 'Stored on the site in the database. The UI never reads it back.'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={labelStyle}>Safety margin (W)</label>
                <input type="number" min="0" value={form.safety_margin_w} onChange={(e) => setForm({ ...form, safety_margin_w: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Noise margin (W)</label>
                <input type="number" min="0" value={form.noise_margin_w} onChange={(e) => setForm({ ...form, noise_margin_w: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Idle floor (W)</label>
                <input type="number" min="0" value={form.idle_floor_w} onChange={(e) => setForm({ ...form, idle_floor_w: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Max per charger (W)</label>
                <input type="number" min="0" value={form.charger_max_w} onChange={(e) => setForm({ ...form, charger_max_w: e.target.value })} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Follow PV min (W)</label>
                <input type="number" min="0" value={form.pv_follow_min_w} onChange={(e) => setForm({ ...form, pv_follow_min_w: e.target.value })} style={inputStyle} />
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#666', margin: '-0.25rem 0 0.75rem' }}>
              Follow PV min is a fleet floor (default 20000 W = 20 kW) so solar mode never writes 0 W and sessions keep running. Also used as the fail-safe when EMS data is stale. Grid assist ignores it.
            </div>

            <div style={{ margin: '1rem 0' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Chargers at this site</div>
              {chargerList.length === 0 && <div style={{ color: '#666', fontSize: '0.9rem' }}>No chargers known yet.</div>}
              {chargerList.map((c) => (
                <label key={c.id} style={{ display: 'block', marginBottom: '0.35rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedChargers.includes(c.id)}
                    onChange={() => toggleCharger(c.id)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {c.display_name || c.id}
                  <span style={{ color: '#888', marginLeft: '0.35rem', fontSize: '0.85rem' }}>
                    {c.connected ? 'online' : 'offline'}
                  </span>
                </label>
              ))}
            </div>

            {selectedId ? (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>Save</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAuto(!selectedSite?.auto_control)}
                  disabled={!selectedSite?.ems_token_configured}
                >
                  {selectedSite?.auto_control ? 'Disable auto control' : 'Enable auto control'}
                </button>
                <button type="button" className="btn btn-danger" onClick={handleDelete}>Delete site</button>
              </div>
            ) : (
              <button type="submit" className="btn btn-primary" disabled={saving}>Create site</button>
            )}
            {selectedId && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginLeft: '0.5rem' }}
                onClick={() => { setSelectedId(null); setForm(emptyForm); setSelectedChargers([]); setStatus(null); }}
              >
                New site
              </button>
            )}
          </form>
        </div>

        <div className="card">
          <h3>Live control</h3>
          {!selectedId && <p style={{ color: '#666' }}>Select or create a site.</p>}
          {selectedId && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ marginBottom: '0.35rem' }}>
                  <strong>Mode:</strong> {control?.control_mode === 'pv_follow' ? 'Follow PV (PV − house excl. EV)' : 'Grid assist (PV + allowed import)'}
                </div>
                <strong>Budget:</strong> {kw(control?.budget_w)} &nbsp;
                <strong>Fail-safe:</strong> {kw(control?.stale_budget_w)}
                {control?.control_mode === 'pv_follow' && (
                  <div style={{ marginTop: '0.35rem', color: '#555', fontSize: '0.9rem' }}>
                    Min {kw(control.pv_follow_min_w ?? selectedSite?.pv_follow_min_w)} · House excl. EV {kw(control.house_residual_avg_w ?? control.house_residual_w)}
                    {control.house_window_samples != null ? ` (${control.house_window_samples} samples / ${control.house_window_seconds}s)` : ''}
                  </div>
                )}
              </div>
              <div style={{
                padding: '0.75rem',
                borderRadius: 6,
                background: control?.usable ? '#e8f5e9' : '#fff3e0',
                border: `1px solid ${control?.usable ? '#a5d6a7' : '#ffcc80'}`,
                marginBottom: '1rem',
              }}>
                {control?.usable ? 'EMS live data is usable for control.' : `Not usable: ${control?.reason || 'no snapshot yet'}`}
              </div>
              {live && (
                <div style={{ fontSize: '0.95rem', marginBottom: '1rem' }}>
                  <div>PV {kw(live.power_w?.pv)} · Site load {kw(live.power_w?.load)} · EV meter {kw(live.power_w?.ev_charger)}</div>
                  <div>Grid {kw(live.power_w?.grid)} ({live.power_w?.grid < 0 ? 'export' : 'import'}) · Import limit {kw(live.grid?.import_limit_w)}</div>
                  <div>Available import {kw(live.grid?.available_import_w)} · Reading age {live.freshness?.last_reading_age_seconds ?? '—'} s</div>
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Charger</th>
                    <th style={thStyle}>State</th>
                    <th style={thStyle}>Setpoint</th>
                  </tr>
                </thead>
                <tbody>
                  {(control?.chargers || []).map((c) => (
                    <tr key={c.id}>
                      <td style={tdStyle}>{c.display_name || c.id}</td>
                      <td style={tdStyle}>{c.connected ? (c.charging ? 'Charging' : 'Idle') : 'Offline'}</td>
                      <td style={tdStyle}>{kw(control?.allocations?.[c.id])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {control?.allocations && (
                <div style={{ marginTop: '0.75rem', color: '#555', fontSize: '0.85rem' }}>
                  Sum {kw(Object.values(control.allocations).reduce((a, b) => a + b, 0))}
                  {control.budget_w != null ? ` / budget ${kw(control.budget_w)}` : ''}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: 600, margin: '0.6rem 0 0.25rem' };
const inputStyle = { width: '100%', padding: '0.45rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' };
const thStyle = { textAlign: 'left', borderBottom: '1px solid #ddd', padding: '0.35rem' };
const tdStyle = { borderBottom: '1px solid #eee', padding: '0.35rem' };

export default Sites;
